import { toast } from 'vue-sonner';

import { i18n } from '@/i18n';
import { useAppStore } from '@stores/app.store';

export class ApiClientError extends Error {
  constructor(
    message: string,
    public readonly statusCode: number,
    public readonly body: unknown,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

function buildQueryString(params?: Record<string, unknown>): string {
  if (!params) {
    return '';
  }

  const entries = Object.entries(params).filter(
    ([, paramValue]) => paramValue !== undefined && paramValue !== null && paramValue !== '',
  );

  if (entries.length === 0) {
    return '';
  }

  const searchParams = new URLSearchParams();

  for (const [key, value] of entries) {
    searchParams.set(key, String(value));
  }

  return `?${searchParams.toString()}`;
}

function getLocaleHeader(): string {
  try {
    const store = useAppStore();

    return store.locale;
  } catch {
    return 'uz';
  }
}

function getAuthToken(): string | null {
  try {
    const stored = localStorage.getItem('auth');

    if (!stored) {
      return null;
    }

    const parsed = JSON.parse(stored) as { accessToken?: string };

    return parsed.accessToken ?? null;
  } catch {
    return null;
  }
}

function buildHeaders(contentType?: string): Record<string, string> {
  const headers: Record<string, string> = {};

  if (contentType) {
    headers['Content-Type'] = contentType;
  }

  headers['X-Locale'] = getLocaleHeader();

  const token = getAuthToken();

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
}

function translate(key: string): string {
  return i18n.global.t(key);
}

function getStatusMessage(statusCode: number): string | null {
  const statusMessages: Record<number, string> = {
    400: 'errors.badRequest',
    401: 'errors.unauthorized',
    403: 'errors.accessDenied',
    404: 'errors.notFound',
    500: 'errors.serverError',
  };

  const translationKey = statusMessages[statusCode];

  return translationKey ? translate(translationKey) : null;
}

/**
 * The API returns messages already translated through `X-Locale`, and they are
 * far more useful than a status-level phrase — "this code is already taken"
 * versus "Noto'g'ri so'rov". NestJS validation failures arrive as an array.
 */
function extractServerMessage(body: unknown): string | null {
  if (typeof body !== 'object' || body === null) {
    return null;
  }

  const { message } = body as { message?: unknown };

  if (typeof message === 'string' && message.trim() !== '') {
    return message;
  }

  if (Array.isArray(message)) {
    const lines = message.filter(
      (entry): entry is string => typeof entry === 'string' && entry.trim() !== '',
    );

    return lines.length > 0 ? lines.join(', ') : null;
  }

  return null;
}

/**
 * A session that has expired and a server that has broken are not things the
 * user can act on, and a 500's message may describe internals — those keep the
 * generic phrasing. Everything else shows what the server actually said.
 */
function shouldPreferServerMessage(statusCode: number): boolean {
  return statusCode !== 401 && statusCode < 500;
}

function showErrorToast(serverMessage: string | null, statusCode: number, fallback: string): void {
  if (serverMessage !== null && shouldPreferServerMessage(statusCode)) {
    toast.error(serverMessage);

    return;
  }

  toast.error(getStatusMessage(statusCode) ?? serverMessage ?? fallback);
}

/**
 * Reads the error body once, reports it to the user, and returns the error to
 * throw. Every failing branch below goes through here so the toast and the
 * thrown message can never drift apart.
 */
async function buildFailure(response: Response): Promise<ApiClientError> {
  const body: unknown = await response.json().catch(() => null);
  const serverMessage = extractServerMessage(body);
  const message = serverMessage ?? response.statusText;

  showErrorToast(serverMessage, response.status, response.statusText);

  return new ApiClientError(message, response.status, body);
}

let refreshPromise: Promise<void> | null = null;

async function handleUnauthorized(): Promise<void> {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      const { useAuthStore } = await import('@/stores/auth.store');
      const authStore = useAuthStore();

      await authStore.refresh();
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

async function request<T>(
  method: string,
  path: string,
  options?: {
    body?: unknown;
    params?: Record<string, unknown>;
    skipRefresh?: boolean;
    signal?: AbortSignal;
  },
): Promise<T> {
  const url = `/api${path}${buildQueryString(options?.params)}`;
  const headers = buildHeaders('application/json');

  const response = await fetch(url, {
    method,
    headers,
    body: options?.body != null ? JSON.stringify(options.body) : undefined,
    signal: options?.signal,
  });

  if (response.status === 401 && !options?.skipRefresh) {
    try {
      await handleUnauthorized();

      return await request<T>(method, path, { ...options, skipRefresh: true });
    } catch {
      throw await buildFailure(response);
    }
  }

  if (!response.ok) {
    throw await buildFailure(response);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

export const apiClient = {
  /**
   * `signal` lets a caller cancel a superseded read — list screens abort the
   * previous page/filter request so a slow response cannot land after a newer
   * one and put stale rows on screen.
   */
  get<T>(path: string, params?: Record<string, unknown>, signal?: AbortSignal): Promise<T> {
    return request<T>('GET', path, { params, signal });
  },

  post<T>(path: string, body?: unknown): Promise<T> {
    return request<T>('POST', path, { body });
  },

  patch<T>(path: string, body?: unknown): Promise<T> {
    return request<T>('PATCH', path, { body });
  },

  put<T>(path: string, body?: unknown): Promise<T> {
    return request<T>('PUT', path, { body });
  },

  delete(path: string): Promise<void> {
    return request<undefined>('DELETE', path) as Promise<void>;
  },

  async upload<T>(path: string, formData: FormData): Promise<T> {
    const url = `/api${path}`;
    const headers = buildHeaders();

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (response.status === 401) {
      try {
        await handleUnauthorized();

        return await this.upload<T>(path, formData);
      } catch {
        throw await buildFailure(response);
      }
    }

    if (!response.ok) {
      throw await buildFailure(response);
    }

    return response.json() as Promise<T>;
  },

  async downloadBlob(path: string): Promise<Blob> {
    const url = `/api${path}`;
    const headers = buildHeaders();

    const response = await fetch(url, { method: 'GET', headers });

    if (response.status === 401) {
      try {
        await handleUnauthorized();

        return await this.downloadBlob(path);
      } catch {
        throw await buildFailure(response);
      }
    }

    if (!response.ok) {
      throw await buildFailure(response);
    }

    return response.blob();
  },
};
